import React, { memo, useMemo } from 'react';
import { Theme, Typography, useMediaQuery } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'i18n';
import CircleSector from 'components/common/CircleSector';

export interface IFamilyProgramsDescription {
  mainTitleKey?: string;
  market: string;
  marketTextKey?: string;
  mainTitleStyle?: any;
  titleKey: string;
  titleStyle?: any;
  supportByImageScr?: string;
  backgroundPosition?: string;
}

const useStyles = makeStyles()((theme) => ({
  root: {
    position: 'relative',
    maxWidth: 370,
    boxSizing: 'content-box',
    padding: '161px 61px 161px 114px',
    [theme.breakpoints.up('md')]: {
      // marginLeft: 60,
    },
    [theme.breakpoints.down('md')]: {
      padding: '87px 32px 91px 32px',
    },
  },
  market: {
    fontSize: 18,
    fontWeight: 600,
    lineHeight: '120%',
    textTransform: 'uppercase',
    letterSpacing: '-0.02em',
    marginBottom: 16,
    [theme.breakpoints.down('sm')]: {
      maxWidth: 200,
      fontSize: 18,
    },
  },
  targetAge: {
    fontSize: 34,
    fontWeight: 700,
    lineHeight: '120%',
    letterSpacing: '-0.02em',
    [theme.breakpoints.down('sm')]: {
      maxWidth: 200,
      fontSize: 24,
    },
  },
  imageContainer: {
    position: 'relative',
    width: 87,
    height: 87,
  },
  image: {
    width: '100%',
  },
  sponsoredBy: {
    maxWidth: 130,
    marginRight: 8,
  },
}));

const PromoProgramsDescription: React.FC<React.PropsWithChildren<IFamilyProgramsDescription>> = (props) => {
  const {
    mainTitleKey,
    market,
    marketTextKey,
    mainTitleStyle,
    titleKey,
    titleStyle,
    supportByImageScr,
    backgroundPosition,
  } = props;
  const { classes } = useStyles();
  const [t] = useTranslation('public');
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const isSmMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const topPosition1 = useMemo(
    () => (backgroundPosition === 'center' && (isMobile ? 34 : 76)) || (isMobile ? 34 : 119),
    [backgroundPosition, isMobile],
  );
  const rightPosition1 = useMemo(
    () => (backgroundPosition === 'center' && (isMobile ? -20 : 90)) || (isMobile ? -20 : 66),
    [backgroundPosition, isMobile],
  );
  const topPosition2 = useMemo(
    () => (backgroundPosition === 'center' && (isMobile ? 81 : 137)) || (isMobile ? 81 : 180),
    [backgroundPosition, isMobile],
  );
  const rightPosition2 = useMemo(
    () => (backgroundPosition === 'center' && (isMobile ? -47 : 55)) || (isMobile ? -47 : 30),
    [backgroundPosition, isMobile],
  );
  const mainTitle = useMemo(() => {
    if (mainTitleKey) return t(mainTitleKey);
    return marketTextKey ? t(marketTextKey) : market;
  }, [mainTitleKey, market, marketTextKey, t]);

  return (
    <div className={classes.root}>
      <Typography
        className={classes.market}
        {...(mainTitleStyle && { style: { ...mainTitleStyle } })}
        component="h1"
        color="secondary"
      >
        {mainTitle}
      </Typography>
      <Typography
        className={classes.targetAge}
        {...(!isSmMobile && titleStyle && { style: { ...titleStyle } })}
        component="h2"
      >
        {t(titleKey, { market })}
      </Typography>
      {supportByImageScr && (
        <div style={{ display: 'flex', marginTop: 16 }}>
          <Typography className={classes.sponsoredBy} variant="h6" color="secondary">
            {t<string>('promo.sponsoredBy')}
          </Typography>
          <div className={classes.imageContainer}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img className={classes.image} src={supportByImageScr} alt={supportByImageScr} />
          </div>
        </div>
      )}
      <CircleSector
        top={topPosition1}
        right={rightPosition1}
        radius={isMobile ? 47 : 61}
        variant="top-left"
        backgroundColor="#4E6DF5"
      />
      <CircleSector
        top={topPosition2}
        right={rightPosition2}
        radius={isMobile ? 27 : 36}
        variant="bottom-right"
        backgroundColor="#F7A9A3"
      />
      <CircleSector
        bottom={isMobile ? 128 : 158}
        left={isMobile ? -37 : 61}
        radius={isMobile ? 27 : 34}
        variant="top-left"
        backgroundColor="#4E6DF5"
      />
      <CircleSector
        bottom={isMobile ? 80 : 97}
        left={isMobile ? -39 : 61}
        radius={isMobile ? 41 : 51}
        variant="bottom-left"
        backgroundColor="#9BB3FA"
      />
    </div>
  );
};

export default memo(PromoProgramsDescription);
